import { useMemo, useState } from "react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import type { DashboardOwner } from "../mock";
import { CallingState } from "../../../components/dashboard/onboarding/CallingState";
import { SuccessState } from "../../../components/dashboard/onboarding/SuccessState";
import styles from "./Onboarding.module.css";

type Props = {
  owner: DashboardOwner;
};

type WizardStep = "agent" | "personalise";
type PersonaliseTab = "talk" | "type";
type CallStage = "idle" | "calling" | "complete";

type Personality = {
  key: string;
  label: string;
  meta: string;
};

const PERSONALITIES: ReadonlyArray<Personality> = [
  { key: "warm", label: "Warm & methodical", meta: "Patient, thorough, encouraging" },
  { key: "sharp", label: "Sharp & quick", meta: "Direct answers, no detour" },
  { key: "playful", label: "Playful", meta: "A bit cheeky, never childish" },
  { key: "strict", label: "Strict & professional", meta: "Polished, on-brand, formal" },
  { key: "thoughtful", label: "Thoughtful", meta: "Reflective, weighs trade-offs" },
  { key: "empathetic", label: "Empathetic", meta: "Reads tone, soft when it counts" },
];

const AGENT_NAME_MAX = 64;

const slugify = (raw: string): string =>
  raw.trim().toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

export const OnboardingSection = ({ owner }: Props): JSX.Element => {
  void owner;
  const [step, setStep] = useState<WizardStep>("agent");
  const [agentName, setAgentName] = useState<string>("Atlas");
  const [personalities, setPersonalities] = useState<ReadonlyArray<string>>(["warm"]);
  const [tab, setTab] = useState<PersonaliseTab>("talk");
  const [phone, setPhone] = useState<string>("+61412345678");
  const [notes, setNotes] = useState<string>("");
  const [callStage, setCallStage] = useState<CallStage>("idle");

  const trimmedName = agentName.trim();
  const slug = useMemo(() => slugify(trimmedName), [trimmedName]);
  const slugValid = slug.length >= 3 && slug.length <= 30 && /^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(slug);
  const agentValid = trimmedName.length > 0 && slugValid;

  const togglePersonality = (key: string): void => {
    setPersonalities((prev): ReadonlyArray<string> =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  const onPrimary = (): void => {
    if (step === "agent") {
      if (agentValid) setStep("personalise");
      return;
    }
    // step === "personalise"
    if (callStage === "complete") return;
    setCallStage("complete");
  };

  const onBack = (): void => {
    if (callStage !== "idle") {
      setCallStage("idle");
      return;
    }
    if (step === "personalise") setStep("agent");
  };

  const onRetry = (): void => {
    setCallStage("idle");
  };

  const startCall = (): void => {
    setCallStage("calling");
  };

  const primaryLabel = ((): string => {
    if (step === "agent") return "Continue";
    if (callStage === "complete") return "Open dashboard";
    return "Launch instance";
  })();

  const primaryDisabled = step === "agent"
    ? !agentValid
    : tab === "talk" && callStage === "idle"; // talk requires a call before launch

  return (
    <div className={styles.page}>
      <ProgressDots step={step} />

      <div className={styles.card}>
        {step === "agent" ? (
          <AgentPanel
            agentName={agentName}
            slug={slug}
            slugValid={slugValid}
            trimmed={trimmedName}
            personalities={personalities}
            onName={setAgentName}
            onTogglePersonality={togglePersonality}
          />
        ) : (
          <PersonalisePanel
            agentName={trimmedName}
            tab={tab}
            onTab={(t: PersonaliseTab): void => {
              if (callStage !== "idle") return;
              setTab(t);
            }}
            phone={phone}
            onPhone={setPhone}
            notes={notes}
            onNotes={setNotes}
            callStage={callStage}
            onStartCall={startCall}
            onRetry={onRetry}
            onCallComplete={(): void => setCallStage("complete")}
          />
        )}

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onBack}
            disabled={step === "agent"}
          >
            Back
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={onPrimary}
            disabled={primaryDisabled}
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Progress dots ────────────────────────────────────────────────
const ProgressDots = ({ step }: { step: WizardStep }): JSX.Element => {
  const items: ReadonlyArray<{ key: WizardStep; label: string }> = [
    { key: "agent", label: "Agent" },
    { key: "personalise", label: "Personalise" },
  ];
  const activeIndex = items.findIndex((i) => i.key === step);
  return (
    <div className={styles.progress} aria-label="Onboarding progress">
      {items.map((item, idx) => {
        const state = idx < activeIndex ? "done" : idx === activeIndex ? "active" : "future";
        return (
          <div key={item.key} className={styles.progressItem}>
            <span
              className={`${styles.progressDot} ${
                state === "done"
                  ? styles.progressDotDone
                  : state === "active"
                    ? styles.progressDotActive
                    : ""
              }`}
            />
            <span
              className={`${styles.progressLabel} ${
                state === "active" ? styles.progressLabelActive : ""
              }`}
            >
              {item.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ── Step 1: Agent ────────────────────────────────────────────────
type AgentPanelProps = {
  agentName: string;
  slug: string;
  slugValid: boolean;
  trimmed: string;
  personalities: ReadonlyArray<string>;
  onName: (v: string) => void;
  onTogglePersonality: (key: string) => void;
};

const AgentPanel = ({
  agentName,
  slug,
  slugValid,
  trimmed,
  personalities,
  onName,
  onTogglePersonality,
}: AgentPanelProps): JSX.Element => (
  <div className={styles.panel}>
    <div className={styles.panelHead}>
      <span className={styles.eyebrow}>Step 1</span>
      <h2 className={styles.heroHeadline}>
        Meet{" "}
        {trimmed.length > 0 ? (
          <span className={styles.heroName}>{trimmed}</span>
        ) : (
          <span className={styles.heroName}>your agent</span>
        )}
        .
      </h2>
      <p className={styles.heroSub}>
        Give it a name and a vibe. We'll handle the rest.
      </p>
    </div>

    <div className={styles.field}>
      <label className={styles.label} htmlFor="dashboard-onb-agent-name">Agent name</label>
      <input
        id="dashboard-onb-agent-name"
        className={styles.input}
        value={agentName}
        onChange={(e): void => onName(e.target.value.slice(0, AGENT_NAME_MAX))}
        placeholder="e.g. Atlas, Pebbles, Luna, Otis"
        maxLength={AGENT_NAME_MAX}
        autoComplete="off"
      />
      {trimmed.length > 0 ? (
        slugValid ? (
          <span className={styles.slugPreview}>
            <span className={styles.slugLabel}>Server name</span>
            <code className={styles.slugCode}>{slug}</code>
          </span>
        ) : (
          <span className={styles.errorInline}>
            Use 3-30 chars: letters, numbers, dashes (no leading/trailing dash).
          </span>
        )
      ) : (
        <span className={styles.hint}>We'll normalise it for the technical handle.</span>
      )}
    </div>

    <div className={styles.field}>
      <label className={styles.label}>Personality</label>
      <span className={styles.hint}>Pick any that fit. They blend into the tone.</span>
      <div className={styles.personalityGrid}>
        {PERSONALITIES.map((p) => {
          const selected = personalities.includes(p.key);
          return (
            <button
              key={p.key}
              type="button"
              className={`${styles.personalityTile} ${selected ? styles.personalityTileSelected : ""}`}
              onClick={(): void => onTogglePersonality(p.key)}
              aria-pressed={selected}
            >
              <span className={styles.personalityLabel}>{p.label}</span>
              <span className={styles.personalityMeta}>{p.meta}</span>
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

// ── Step 2: Personalise ──────────────────────────────────────────
type PersonalisePanelProps = {
  agentName: string;
  tab: PersonaliseTab;
  onTab: (t: PersonaliseTab) => void;
  phone: string;
  onPhone: (v: string) => void;
  notes: string;
  onNotes: (v: string) => void;
  callStage: CallStage;
  onStartCall: () => void;
  onRetry: () => void;
  onCallComplete: () => void;
};

const PersonalisePanel = ({
  agentName,
  tab,
  onTab,
  phone,
  onPhone,
  notes,
  onNotes,
  callStage,
  onStartCall,
  onRetry,
  onCallComplete,
}: PersonalisePanelProps): JSX.Element => {
  const isCallingOrDone = callStage !== "idle";

  return (
    <div className={styles.panel}>
      {!isCallingOrDone && (
        <div className={styles.tabs} role="tablist" aria-label="Personalise mode">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "talk"}
            className={`${styles.tab} ${tab === "talk" ? styles.tabActive : ""}`}
            onClick={(): void => onTab("talk")}
          >
            Talk it through
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "type"}
            className={`${styles.tab} ${tab === "type" ? styles.tabActive : ""}`}
            onClick={(): void => onTab("type")}
          >
            Type it out
          </button>
        </div>
      )}

      <div className={styles.tabBody}>
        {callStage === "calling" ? (
          <div className={styles.fadeIn} key="calling">
            <CallingState
              agentName={agentName}
              phoneE164={phone}
              onComplete={onCallComplete}
              onRetry={onRetry}
            />
          </div>
        ) : callStage === "complete" ? (
          <div className={styles.fadeIn} key="complete">
            <SuccessState agentName={agentName} />
          </div>
        ) : tab === "talk" ? (
          <div className={styles.fadeIn} key="talk">
            <TalkContent phone={phone} onPhone={onPhone} onStartCall={onStartCall} />
          </div>
        ) : (
          <div className={styles.fadeIn} key="type">
            <TypeContent notes={notes} onNotes={onNotes} />
          </div>
        )}
      </div>
    </div>
  );
};

const TalkContent = ({
  phone,
  onPhone,
  onStartCall,
}: {
  phone: string;
  onPhone: (v: string) => void;
  onStartCall: () => void;
}): JSX.Element => (
  <div className={styles.talk}>
    <span className={styles.eyebrowAccent}>Recommended</span>
    <h2 className={styles.tabHeadline}>Have your agent call you.</h2>
    <p className={styles.tabSub}>
      Two minutes. Conversational. We'll figure out who you are, what you do,
      and how your agent should help.
    </p>

    <div className={styles.phoneRow}>
      <PhoneInput
        defaultCountry="au"
        value={phone}
        onChange={(v: string): void => onPhone(v)}
        className={styles.phoneShell ?? ""}
      />
      <button
        type="button"
        className={styles.callCta}
        onClick={onStartCall}
        disabled={phone.replace(/\D+/g, "").length < 6}
      >
        Have your agent call me
      </button>
    </div>

    <p className={styles.fineprint}>
      We'll call within 30 seconds. One call, then we're done.
    </p>

    <ul className={styles.bullets}>
      <li className={styles.bullet}>
        <span className={styles.bulletDot} aria-hidden />
        <span>Picks up tone, priorities, and the things you'd rather it never do.</span>
      </li>
      <li className={styles.bullet}>
        <span className={styles.bulletDot} aria-hidden />
        <span>Skip if you'd rather type — switch tabs above.</span>
      </li>
    </ul>
  </div>
);

const TypeContent = ({
  notes,
  onNotes,
}: {
  notes: string;
  onNotes: (v: string) => void;
}): JSX.Element => {
  const charCount = notes.length;
  return (
    <div className={styles.type}>
      <h2 className={styles.tabHeadline}>Or write a paragraph if you'd rather.</h2>
      <p className={styles.tabSub}>
        Tell us what your agent should know about you upfront.
      </p>

      <textarea
        className={styles.textarea}
        rows={8}
        value={notes}
        onChange={(e): void => onNotes(e.target.value)}
        placeholder="Tell us how you'd like your agent to behave. Tone, priorities, things to avoid, what you do for work — anything that helps us tune it."
      />
      <span className={styles.counter}>
        {charCount} chars · optional, but the more we know, the better the first call goes.
      </span>
    </div>
  );
};
